interface CreatePaymentResponse {
  success: boolean;
  priceWei: string;
  priceEth?: number | null;
  receiverAddress: string;
  chainId: string;
  chainNamespace?: string;
  chainReference?: string;
  chainDisplayName?: string;
  alreadyUnlocked?: boolean;
  paymentId?: string;
  status?: string;
}

interface PaymentStatusResponse {
  success: boolean;
  payment: {
    paymentId: string;
    status: string;
    amountWei?: string | null;
    txHash?: string | null;
    chain?: string | null;
    receiverAddress?: string | null;
    fromAddress?: string | null;
    consumedAt?: string | null;
    interpretationId?: string | null;
    metadata?: Record<string, any> | null;
  };
}

interface ConfirmPaymentResponse {
  success: boolean;
  payment: {
    payment_id: string;
    status: string;
    tx_hash?: string | null;
    amount_wei?: string | null;
    receiver_address?: string | null;
    from_address?: string | null;
    consumed_at?: string | null;
  };
}

export class PaymentError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'PaymentError';
    this.status = status;
  }
}

const parseJson = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('Failed to parse JSON response:', error);
    return null;
  }
};

export const createInterpretationPayment = async (params: { fid: number; deckKey: string }): Promise<CreatePaymentResponse> => {
  const response = await fetch('/api/payments', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action: 'create', ...params }),
  });

  if (!response.ok) {
    const errorBody = await parseJson(response);
    const message = errorBody?.error || 'Failed to initiate payment flow';
    throw new PaymentError(message, response.status);
  }

  const data = (await response.json()) as CreatePaymentResponse;
  return data;
};

export const fetchPaymentStatus = async (paymentId: string): Promise<PaymentStatusResponse> => {
  const response = await fetch(`/api/payments?paymentId=${encodeURIComponent(paymentId)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorBody = await parseJson(response);
    const message = errorBody?.error || 'Failed to fetch payment status';
    throw new PaymentError(message, response.status);
  }

  const data = (await response.json()) as PaymentStatusResponse;
  return data;
};

export const confirmInterpretationPayment = async (params: { fid: number; deckKey: string; txHash: string }): Promise<ConfirmPaymentResponse> => {
  const response = await fetch('/api/payments', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action: 'confirm', ...params }),
  });

  if (!response.ok) {
    const errorBody = await parseJson(response);
    const message = errorBody?.error || 'Failed to confirm payment';
    throw new PaymentError(message, response.status);
  }

  const data = (await response.json()) as ConfirmPaymentResponse;
  return data;
};

export class PaymentCancelledError extends Error {
  constructor(message = 'Payment was cancelled or rejected') {
    super(message);
    this.name = 'PaymentCancelledError';
  }
}

export class PaymentTimeoutError extends Error {
  constructor(message = 'Payment confirmation timed out') {
    super(message);
    this.name = 'PaymentTimeoutError';
  }
}
